import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createChart, createSeriesMarkers, LineSeries, PriceScaleMode,
  type IChartApi, type ISeriesApi, type ISeriesMarkersPluginApi,
  type LineData, type Time, type UTCTimestamp, type WhitespaceData,
} from 'lightweight-charts';
import { analyserFetchJSON, isFetchAbortedError } from '../features/shared/analyserApi';
import {
  alertMarkerPoints, alertSeriesRatioPoints, fmtAlertSeg, fmtMarkerClock, fmtPct, fmtPrice, isTransientLoadError,
  upbitSegsBesideMarker,
  kstMonthDay, liqCascadeCumLabels, pctRankColors, LANE_COLORS, LANES,
  LIQ_VISION_STRICT_PEERS, LIQ_VISION_PEER_COLOR,
  type AlertChartSeries, type AlertChartsResponse, type BaselineMode, type LadderDayItem,
  type LadderDaysResponse, type LaneKey,
} from '../features/binance/binanceAlertCharts';
import { formatChartTimeKST, fmtKST } from '../features/shared/kstTime';
import { DashboardLocaleProvider, useDashboardLocale, useDashboardT } from '../features/shared/dashboardLocale';
import { dashboardHomePath, type DashboardLocale } from '../features/shared/dashboardMessages';

/**
 * '차트' 페이지 — 오늘(KST) 알림이 온 심볼들의 24시간 1분봉을 **한 차트에** 겹친다.
 *
 * 대시보드 패널의 스파크라인은 한 건(진입~청산)을 보는 것이고, 이 페이지는 그
 * 반대다: 하루 치를 한 축에 올려 놓고 "오늘 이 판이 어떻게 생겼나"를 본다.
 *
 * 한 축에 올리려면 가격을 기준가 대비 **배율**로 바꾼다 (SOPH 0.01 과 ZEC 1200 을
 * 같은 축에 못 둔다). 기준은 창 시작 / 첫 알림 둘 중에 고른다 — 후자면 0% 가
 * 알림 시각이라 레인이 실제로 보는 그림이 된다.
 *
 * **세로축은 로그**다 — 선형 %면 기준가가 한참 아래일 때(펌프 후 되돌림) 같은
 * 낙폭이 부풀어 보인다. rung12 마커가 +120% 에 찍히고 선이 +42% 까지 내려온
 * 실제 가격 낙폭은 142/215−1 = −34% 인데, 선형 축이면 "73칸 급락"으로 읽힌다.
 * 로그축이면 −30% 는 차트 어디서든 같은 픽셀 높이로 떨어진다 (2026-09-10 유저).
 *
 * 색은 **% 순위 그라데이션**이다 — 그 창에서 가장 많이 오른 심볼이 레인
 * 정체색(업숏 파랑·청캐 노랑), 낮아질수록 옅어져 최하위가 흰색(pctRankColors).
 *
 * 레인마다 "심볼 목록"·"사건 마커"의 뜻이 다르다 — 업숏은 signal_alert(KR
 * segment_rise), 청캐는 liq_trigger(같은 perp 재발동, entered=진입/skip_reason).
 * 둘 다 analyser `/alert-charts?lane=` 가 갈라 준다(2026-09-08, 업숏을 본따 완성).
 *
 * 가격은 Binance perp 마크가격이다 — 업비트 원화 1분봉은 수집하지 않는다.
 */

const REFRESH_MS = 30_000;
const MUTED = '#8b7d5a';
const CHART_H = 460;
// 산 구간 — 차트 위에서 한눈에 떨어지게 (레인 선·청캐 화살표와 색 분리).
const MOUNTAIN_ACCENTS = [
  { solid: '#ffd166', fill: 'rgba(255, 209, 102, 0.45)', edge: 'rgba(255, 209, 102, 0.95)' },
  { solid: '#ff7a59', fill: 'rgba(255, 122, 89, 0.45)', edge: 'rgba(255, 122, 89, 0.95)' },
  { solid: '#7ee787', fill: 'rgba(126, 231, 135, 0.42)', edge: 'rgba(126, 231, 135, 0.95)' },
  { solid: '#79c0ff', fill: 'rgba(121, 192, 255, 0.42)', edge: 'rgba(121, 192, 255, 0.95)' },
] as const;
const mountainAccent = (i: number) => MOUNTAIN_ACCENTS[i % MOUNTAIN_ACCENTS.length];
// 과거창(48h) 은 서버가 요청 안에서 안 만든다 — 백그라운드로 만들고 캐시하며
// 그 사이엔 `warming:true` 로 즉답한다(analyser alert_charts.go historicCharts).
// 그래서 개별 요청은 빠르지만, 완성될 때까지 프론트가 계속 다시 물어야 한다
// (2026-09-09 유저: "업숏 사다리 누르면 HTTP 504 뜬다" — CloudFront
// origin_read_timeout 30s 컷을 백그라운드로 피했다). 502/503/504·타임아웃도
// 같은 취급 — 에러가 아니라 "아직" 이다(2026-09-08 유저: "에러 내지 말고 로딩중").
const HISTORIC_TIMEOUT_MS = 30_000;
const HISTORIC_MAX_RETRIES = 150; // ~6분(서버 빌드 상한) 동안 2.5초 간격 폴링
const HISTORIC_RETRY_DELAY_MS = 2_500;

/** 포커스가 다른 심볼에 있을 때 이 선·화살표를 흐리게. laneShades 는 hsl() 을 준다. */
const dimColor = (c: string): string => {
  if (c.startsWith('hsl(')) return c.replace(/^hsl\(/, 'hsla(').replace(/\)$/, ' / 22%)');
  const m = /^#?([0-9a-f]{6})$/i.exec(c.trim());
  if (m) {
    const n = parseInt(m[1], 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, 0.22)`;
  }
  return c;
};

type ChartPointLW = LineData<UTCTimestamp> | WhitespaceData<UTCTimestamp>;

type LWMarker = {
  time: UTCTimestamp;
  position: 'aboveBar' | 'belowBar';
  color: string;
  shape: 'arrowDown' | 'arrowUp' | 'circle' | 'square';
  text: string;
};

/**
 * 한 심볼의 화살표 마커들. 업숏은 각 알림의 급등폭 %(segment_pct), 청캐는 그
 * 캐스케이드의 **누적 개별청산 건수**("누적 N" — 2026-09-09, 옛 "N회" 트리거
 * 수 대체). ≥100 부터 v2 사다리 진입(rung = 누적//25). 6h quiet · 산리셋
 * 경계에서 리셋 (liqCascadeCumLabels — 라이브 cascade_span 과 일치).
 *
 * 산 경계(▲▼)는 DOM 음영 호스트가 그린다 — 시리즈 마커에 빈 text 로 넣으면
 * 같은 시각의 "누적 N" 을 lightweight-charts 가 덮어쓴다 (2026-09-30).
 */
/** 겹롱은 보유가 수 초~5분이라 진입·청산이 같은 1분봉에 앉는다.
 *  업숏과 같이 진입은 가격 위, 청산은 가격 아래라 글자가 겹치지 않는다. */
const gyeopTradeMarkers = (
  s: AlertChartSeries,
  color: string,
  labels: { entry: string; stop: string; exit: string },
): LWMarker[] => {
  const closes = s.c ?? [];
  if (!closes.length) return [];
  const t0 = Math.floor(s.t0 / 1000);
  const step = s.step_s > 0 ? s.step_s : 60;
  const end = t0 + Math.max(0, closes.length - 1) * step;
  const snap = (ms: number) => {
    const sec = Math.floor(ms / 1000);
    const snapped = t0 + Math.floor((sec - t0) / step) * step;
    return Math.max(t0, Math.min(end, snapped));
  };
  const clock = (ms: number) => {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Seoul',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(new Date(ms));
    const g = (typ: string) => parts.find((p) => p.type === typ)?.value ?? '';
    return `${g('month')}-${g('day')} ${g('hour')}:${g('minute')}:${g('second')}`;
  };
  const out: LWMarker[] = [];
  (s.alerts ?? []).forEach((a) => {
    if (a.type !== 'entry' && a.type !== 'exit' && a.type !== 'stop') return;
    const entry = a.type === 'entry';
    const word = a.type === 'stop' ? labels.stop : entry ? labels.entry : labels.exit;
    out.push({
      time: snap(a.t) as UTCTimestamp,
      position: entry ? 'aboveBar' : 'belowBar',
      color,
      shape: entry ? 'arrowDown' : 'arrowUp',
      text: `${word} ${clock(a.t)}`,
    });
  });
  return out;
};

const buildSeriesMarkers = (
  s: AlertChartSeries,
  ln: LaneKey,
  color: string,
  mountainStartsSec: number[] = [],
  labels: { entry: string; stop: string; exit: string; cum: string } = {
    entry: '진입', stop: '스탑', exit: '청산', cum: '누적',
  },
): LWMarker[] => {
  if (ln === 'gyeop') return gyeopTradeMarkers(s, color, labels);
  const pts = alertMarkerPoints(s);
  // 청캐: 누적청산 라벨 + LSK·IRYS 반사실 롱 진입·청산(초).
  const liqFire = ln === 'liq'
    ? pts.filter((p) => p.markerType !== 'entry' && p.markerType !== 'exit')
    : pts;
  const liqLabels = ln === 'liq'
    ? liqCascadeCumLabels(liqFire, { mountainStartsSec, cumWord: labels.cum })
    : null;
  const marks = (ln === 'liq' ? liqFire : pts).map((p, i) => {
    let text = '';
    if (ln === 'liq') {
      text = liqLabels![i];
    } else if ((ln === 'bh' || ln === 'upbit') && p.markerType === 'entry') {
      text = `${labels.entry} ${fmtMarkerClock(p.eventMs ?? p.time * 1000)}`;
    } else if ((ln === 'bh' || ln === 'upbit') && p.markerType === 'stop') {
      text = `${labels.stop} ${fmtMarkerClock(p.eventMs ?? p.time * 1000)}`;
    } else if (ln === 'upbit' && p.markerType === 'exit') {
      text = `${labels.exit} ${fmtMarkerClock(p.eventMs ?? p.time * 1000)}`;
    } else if (p.seg != null) {
      text = fmtAlertSeg(p.seg);
    }
    return {
      time: p.time as UTCTimestamp,
      position: 'aboveBar' as const,
      color,
      shape: 'arrowDown' as const,
      text,
    };
  });
  if (ln === 'liq') {
    return [...marks, ...gyeopTradeMarkers(s, '#3fb950', labels)];
  }
  if (ln !== 'upbit') return marks;
  const beside = new Map<number, LWMarker[]>();
  upbitSegsBesideMarker(s).forEach((e) => {
    const arr = beside.get(e.time) ?? [];
    arr.push({
      time: e.time as UTCTimestamp,
      position: 'aboveBar',
      color,
      shape: 'arrowDown',
      text: fmtAlertSeg(e.seg),
    });
    beside.set(e.time, arr);
  });
  if (!beside.size) return marks;
  const out: LWMarker[] = [];
  marks.forEach((m) => {
    const add = beside.get(m.time as number);
    if (add) {
      out.push(...add);
      beside.delete(m.time as number);
    }
    out.push(m);
  });
  return out;
};

const WAIT_MARKER_COLOR: Record<number, string> = { 15: '#3dd68c', 120: '#d2a8ff' };

/** 15분·120분 확인 진입·청산. 글자만 두면 가격 위 어디인지 안 보인다. */
const waitSeriesMarkers = (
  s: AlertChartSeries,
  waits: NonNullable<AlertChartsResponse['waits']>,
  label: (kind: 'entry' | 'exit', n: number) => string,
): LWMarker[] => {
  const closes = s.c ?? [];
  const t0 = Math.floor(s.t0 / 1000);
  const step = s.step_s > 0 ? s.step_s : 60;
  const end = t0 + Math.max(0, closes.length - 1) * step;
  const snap = (ms: number) => {
    const sec = Math.floor(ms / 1000);
    const snapped = t0 + Math.floor((sec - t0) / step) * step;
    return Math.max(t0, Math.min(end, snapped));
  };
  const out: LWMarker[] = [];
  waits.forEach((w) => {
    const color = WAIT_MARKER_COLOR[w.wait_min] ?? '#e8dcc0';
    out.push({
      time: snap(w.entry_ms) as UTCTimestamp,
      position: 'aboveBar',
      color,
      shape: 'arrowDown',
      text: `${label('entry', w.wait_min)} ${fmtMarkerClock(w.entry_ms)}`,
    });
    out.push({
      time: snap(w.exit_ms) as UTCTimestamp,
      position: 'belowBar',
      color,
      shape: 'arrowUp',
      text: `${label('exit', w.wait_min)} ${fmtMarkerClock(w.exit_ms)}`,
    });
  });
  return out;
};

/** 산의 시작·끝. 가격 바로 위·아래. 차트 틀 상단에 붙이지 않는다. */
const mountainEdgeMarkers = (
  s: AlertChartSeries,
  mountains: NonNullable<AlertChartsResponse['mountains']>,
): LWMarker[] => {
  const closes = s.c ?? [];
  const t0 = Math.floor(s.t0 / 1000);
  const step = s.step_s > 0 ? s.step_s : 60;
  const end = t0 + Math.max(0, closes.length - 1) * step;
  const snap = (ms: number) => {
    const sec = Math.floor(ms / 1000);
    const snapped = t0 + Math.floor((sec - t0) / step) * step;
    return Math.max(t0, Math.min(end, snapped));
  };
  const out: LWMarker[] = [];
  const onChart = (ms: number) => {
    const sec = Math.floor(ms / 1000);
    return sec >= t0 - step && sec <= end + step;
  };
  mountains.forEach((m) => {
    if (!(m.first_ms > 0) || !(m.last_ms > 0)) return;
    const start = m.start_seg ? `${fmtAlertSeg(m.start_seg)} ` : '';
    const finish = m.end_seg ? `${fmtAlertSeg(m.end_seg)} ` : '';
    // 창 밖 끝은 첫 봉·마지막 봉에 시각을 붙이지 않는다. 띠는 가장자리까지 잘린다.
    if (onChart(m.first_ms)) {
      out.push({
        time: snap(m.first_ms) as UTCTimestamp,
        position: 'aboveBar',
        color: '#f0c14b',
        shape: 'arrowDown',
        text: `${start}${fmtMarkerClock(m.first_ms)}`,
      });
    }
    if (onChart(m.last_ms)) {
      out.push({
        time: snap(m.last_ms) as UTCTimestamp,
        position: 'belowBar',
        color: '#f0c14b',
        shape: 'arrowUp',
        text: `${finish}${fmtMarkerClock(m.last_ms)}`,
      });
    }
  });
  return out;
};

/** 범례 한 줄. 크로스헤어를 움직이면 그 시각 값으로 바뀐다. */
type LegendRow = {
  s: AlertChartSeries;
  color: string;
  pct: number | null;
};

/** 차트에 그릴 산 목록 — API mountains(산리셋 분할). 없으면 클러스터 통짜. */
const resolveMountains = (
  selected: LadderDayItem | null,
  data: AlertChartsResponse | null,
): Array<{ firstMs: number; lastMs: number }> => {
  if (!selected) return [];
  const fromApi = (data?.mountains ?? [])
    .filter((m) => m.first_ms > 0 && m.last_ms > 0)
    .map((m) => ({ firstMs: m.first_ms, lastMs: m.last_ms }));
  if (fromApi.length) return fromApi;
  const first = data?.cluster_first_ms ?? selected.first_ms;
  const last = data?.cluster_last_ms ?? selected.last_ms;
  return first > 0 && last > 0 ? [{ firstMs: first, lastMs: last }] : [];
};

/** 산2+ first 시각(초) — 누적 라벨 리셋. 산1 시작은 스킵. */
const mountainResetStartsSec = (
  mtns: Array<{ firstMs: number; lastMs: number }>,
): number[] => mtns.slice(1).map((m) => Math.floor(m.firstMs / 1000));

const isSkrChart = (perp?: string, symbol?: string): boolean =>
  perp === 'SKRUSDT' || symbol === 'KRW-SKR';

const ladderFocus = (s: AlertChartSeries, selected: LadderDayItem | null): boolean => {
  if (!selected) return false;
  const bare = (x: string) => x.replace(/^KRW-/, '');
  return s.symbol === selected.symbol
    || s.perp === selected.perp
    || bare(s.symbol) === bare(selected.symbol);
};


const ChartsBody: React.FC = () => {
  const t = useDashboardT();
  const { locale } = useDashboardLocale();
  const backHref = dashboardHomePath(locale);
  const markerLabels = useMemo(() => ({
    entry: t('charts.marker.entry'),
    stop: t('charts.marker.stop'),
    exit: t('charts.marker.exit'),
    cum: t('charts.cum'),
  }), [t]);
  const waitLabel = useCallback((kind: 'entry' | 'exit', n: number) => (
    t(kind === 'entry' ? 'charts.wait.entry' : 'charts.wait.exit', { n })
  ), [t]);
  const laneName = (key: LaneKey) => {
    if (key === 'upbit') return t('binance.lane.upbit');
    if (key === 'bh') return t('binance.lane.bithumb');
    if (key === 'gyeop') return t('binance.lane.gyeopLong');
    return t('binance.lane.liq');
  };
  const [data, setData] = useState<AlertChartsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [auto, setAuto] = useState(true);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const [lane, setLane] = useState<LaneKey>('upbit');
  const [selectedLadder, setSelectedLadder] = useState<LadderDayItem | null>(null);
  const liqFlipTick = lane === 'liq' && !!selectedLadder && (
    selectedLadder.perp === 'LSKUSDT' || selectedLadder.perp === 'IRYSUSDT'
    || selectedLadder.symbol === 'LSKUSDT' || selectedLadder.symbol === 'IRYSUSDT'
  );
  const tickLane = lane === 'gyeop' || liqFlipTick;
  // 기본은 '고점' — 현재값이 곧 고점 대비 낙폭이라 대시보드 −%와 바로 맞는다
  // (2026-09-10 유저: "30% 떨어졌으면 30% 떨어진 걸로 나와야지").
  const [baseline, setBaseline] = useState<BaselineMode>('peak');
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  /** 범례에 마우스오버한 심볼. */
  const [focus, setFocus] = useState<string | null>(null);
  /** 차트에서 선 위에 마우스오버한 심볼(크로스헤어). 범례 focus 가 우선. */
  const [chartHover, setChartHover] = useState<string | null>(null);
  /** 크로스헤어 시각의 심볼별 % (없으면 마지막 값). */
  const [hoverPct, setHoverPct] = useState<Map<string, number> | null>(null);

  /** 날짜 버튼에서 고른 사건. null = 라이브(오늘~지금). */
  const [ladderDays, setLadderDays] = useState<LadderDayItem[]>([]);
  const [ladderDaysErr, setLadderDaysErr] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const mountainBandRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<Map<string, ISeriesApi<'Line'>>>(new Map());
  // createSeriesMarkers 는 Time(=UTCTimestamp|BusinessDay|string) 으로 돌려준다.
  const markersRef = useRef<Map<string, ISeriesMarkersPluginApi<Time>>>(new Map());
  const fittedRef = useRef(false);

  const laneColor = LANE_COLORS[lane];
  const laneReady = LANES.find((l) => l.key === lane)?.ready ?? false;

  /** 과거창 재시도 횟수 — isRetry 호출이 아니면(새 요청) 0 으로 되돌린다. */
  const historicRetryRef = useRef(0);
  /** 지금 조용히 재시도 중인가 — 에러 대신 이걸로 로딩 문구를 바꿔 보여준다. */
  const [retrying, setRetrying] = useState(false);

  const load = useCallback(async (opts?: { isRetry?: boolean }) => {
    if (!opts?.isRetry) { historicRetryRef.current = 0; setRetrying(false); }
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const query = selectedLadder
        ? `lane=${lane}&center_ms=${selectedLadder.center_ms}`
          + `&first_ms=${selectedLadder.first_ms}&last_ms=${selectedLadder.last_ms}`
          + `&perp=${encodeURIComponent(selectedLadder.perp)}`
        : `lane=${lane}&hours=24`;
      const next = await analyserFetchJSON<AlertChartsResponse>(
        'binance',
        `/alert-binance-short-live/alert-charts?${query}`,
        { timeoutMs: selectedLadder ? HISTORIC_TIMEOUT_MS : 60000, signal: ctrl.signal },
      );

      // 과거창이 아직 백그라운드로 만들어지는 중(warming) — 완성될 때까지 폴링.
      // 화면엔 직전 창이 남아 있고 "불러오는 중" 배너만 뜬다.
      if (selectedLadder && next.ok && next.warming
        && historicRetryRef.current < HISTORIC_MAX_RETRIES) {
        historicRetryRef.current += 1;
        setRetrying(true);
        window.setTimeout(() => {
          if (ctrl.signal.aborted) return;
          void load({ isRetry: true });
        }, HISTORIC_RETRY_DELAY_MS);
        return; // loading 유지
      }

      historicRetryRef.current = 0;
      setRetrying(false);
      setData(next);
      setError(next.ok ? null : (next.error || t('charts.err.server')));
      setFetchedAt(Date.now());
      setLoading(false);
    } catch (e) {
      if (isFetchAbortedError(e)) return; // 새 요청에 밀려 취소된 것 — 그쪽이 처리한다
      const msg = e instanceof Error ? e.message : String(e);
      if (selectedLadder && isTransientLoadError(msg)
        && historicRetryRef.current < HISTORIC_MAX_RETRIES) {
        // 48시간치를 그 자리에서 긁는 요청이라 가끔 늦는다 — 에러로 보여주는
        // 대신 "불러오는 중"을 유지한 채 조용히 다시 시도한다(2026-09-08 유저).
        historicRetryRef.current += 1;
        setRetrying(true);
        window.setTimeout(() => {
          if (ctrl.signal.aborted) return; // 그 사이 다른 요청이 시작됐다
          void load({ isRetry: true });
        }, HISTORIC_RETRY_DELAY_MS);
        return; // loading=true 유지 — finally 없이 여기서 끝낸다
      }
      historicRetryRef.current = 0;
      setRetrying(false);
      setError(msg);
      setLoading(false);
    }
  }, [lane, selectedLadder, t]);

  // 사다리 날짜 목록 — 레인마다 정의가 다르다(업숏=segment rung, 청캐=캐스케이드
  // 누적청산 ≥100). 레인 바뀔 때 + 라이브 폴링에 같이 태워 새 캐스케이드가
  // 페이지 새로고침 없이 버튼에 뜨게 한다 (2026-09-10 유저).
  const loadLadderDays = useCallback(async () => {
    if (!laneReady) { setLadderDays([]); setLadderDaysErr(null); return; }
    try {
      const r = await analyserFetchJSON<LadderDaysResponse>(
        'binance', `/alert-binance-short-live/ladder-days?lane=${lane}`, { timeoutMs: 90000 },
      );
      if (r.ok) { setLadderDays(r.episodes); setLadderDaysErr(null); } else {
        setLadderDaysErr(r.error || t('charts.err.ladder'));
      }
    } catch (e) {
      if (!isFetchAbortedError(e)) {
        setLadderDaysErr(e instanceof Error ? e.message : String(e));
      }
    }
  }, [lane, laneReady, t]);

  useEffect(() => {
    setLoading(true);
    void load();
    void loadLadderDays();
    return () => abortRef.current?.abort();
  }, [load, loadLadderDays]);

  // 사다리 바꾸면 옛 선을 지우고 fit 을 다시 — 워밍 중 직전 창에 fit 해 두면
  // 새 데이터가 와도 안 맞고 오른쪽(끝)만 보이는 것처럼 뛴다 (LSK 09-12→14).
  const ladderKey = selectedLadder
    ? `${selectedLadder.symbol}:${selectedLadder.center_ms}`
    : '';
  useEffect(() => {
    fittedRef.current = false;
    if (ladderKey) setData(null);
    // 청캐 사다리 버튼 % = 산 시가→고가. 기본 '고점' 기준이면 −95%처럼 보여
    // 버튼 +1225% 와 어긋난다 → 창시작으로 맞춰 같은 축.
    if (ladderKey && lane === 'liq') setBaseline('window');
  }, [ladderKey, lane]);

  useEffect(() => {
    // 과거창을 보는 동안은 자동갱신을 쉰다 — 지난 날짜는 값이 안 바뀌고,
    // 매번 그 자리에서 통째로 다시 긁는 요청(수 초)을 30초마다 반복할
    // 이유가 없다.
    if (!auto || selectedLadder) return undefined;
    const id = window.setInterval(() => {
      void load();
      void loadLadderDays();   // 사다리 날짜 버튼도 같이 (새 캐스케이드 반영)
    }, REFRESH_MS);
    return () => window.clearInterval(id);
  }, [auto, load, loadLadderDays, selectedLadder]);

  // 탭을 다시 열면 즉시 한 번 — 라이브 보기에서만(과거창은 안 바뀐다).
  useEffect(() => {
    if (selectedLadder) return undefined;
    const onVis = () => {
      if (document.visibilityState === 'visible') { void load(); void loadLadderDays(); }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [load, loadLadderDays, selectedLadder]);

  /** 실제로 강조할 심볼 — 범례 마우스오버가 차트 선 마우스오버보다 우선. */
  const activeFocus = focus ?? chartHover;

  /** 그릴 수 있는 심볼만. 봉이 0 인 건(perp 미상장 등)은 아래에 따로 적는다. */
  const drawable = useMemo(
    () => (data?.series ?? []).filter((s) => s.n_bars > 0),
    [data],
  );
  const undrawable = useMemo(
    () => (data?.series ?? []).filter((s) => s.n_bars === 0),
    [data],
  );

  // 라인 데이터는 **기준가 대비 배율**(1.0 = 기준)이다. 세로축은 로그라서 같은
  // 낙폭이 차트 어디서든 같은 픽셀로 떨어진다. 값 → % 는 `(v−1)×100`.
  // 청캐는 고가(h) — 버튼 시가→고가·last 윅(BABY +10936)이 선에 보이게.
  const pointsBySymbol = useMemo(() => {
    const m = new Map<string, ChartPointLW[]>();
    const px = lane === 'liq' ? 'h' as const : 'c' as const;
    drawable.forEach((s) => {
      m.set(s.symbol, alertSeriesRatioPoints(s, baseline, px).map((p) => (
        p.value == null
          ? { time: p.time as UTCTimestamp }
          : { time: p.time as UTCTimestamp, value: p.value }
      )));
    });
    return m;
  }, [drawable, baseline, lane]);

  /** 심볼별 현재 % (라인 마지막 값, 배율→%) — 색 순위와 범례 기본값의 근거. */
  const rankMetrics = useMemo(() => drawable.map((s) => {
    const pts = pointsBySymbol.get(s.symbol) ?? [];
    for (let j = pts.length - 1; j >= 0; j -= 1) {
      const v = (pts[j] as LineData<UTCTimestamp>).value;
      if (typeof v === 'number' && Number.isFinite(v)) return (v - 1) * 100;
    }
    return null;
  }), [drawable, pointsBySymbol]);

  // %가 가장 높은 심볼 = 레인색, 낮아질수록 옅어져 최하위 = 흰색 (2026-09-08 유저).
  const colors = useMemo(
    () => pctRankColors(laneColor, rankMetrics),
    [laneColor, rankMetrics],
  );

  // 산2+ 시작(초) — 청캐 누적 라벨이 산리셋에서 끊기게.
  const mtnResetSec = useMemo(
    () => mountainResetStartsSec(resolveMountains(selectedLadder, data)),
    [selectedLadder, data],
  );

  // --- 차트 생성 (한 번) -----------------------------------------------------
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return undefined;
    // ref.current 를 정리 함수에서 바로 읽으면 eslint 가 경고한다(그때는 다른
    // 값일 수 있다). Map 자체는 컴포넌트 생애 내내 같은 객체라 여기서 잡아 둔다.
    const live = seriesRef.current;
    const marks = markersRef.current;
    const chart = createChart(box, {
      width: box.clientWidth,
      height: CHART_H,
      autoSize: true,
      layout: {
        background: { color: 'transparent' },
        textColor: '#8b949e',
        fontSize: 11,
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: 'rgba(139,148,158,0.08)' },
        horzLines: { color: 'rgba(139,148,158,0.08)' },
      },
      // 로그 축 — 라인은 배율(1.0=기준). 선형 %면 기준가가 아래일수록 같은
      // 낙폭이 부풀어 보인다 (rung12 +120%→+42% 의 실제 낙폭은 −34%).
      rightPriceScale: { borderColor: '#30363d', mode: PriceScaleMode.Logarithmic },
      timeScale: {
        borderColor: '#30363d',
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: (t: number) => formatChartTimeKST(t, false),
      },
      crosshair: {
        mode: 1,
        vertLine: { color: MUTED, width: 1, style: 3, labelBackgroundColor: '#1c1b18' },
        horzLine: { color: MUTED, width: 1, style: 3, labelBackgroundColor: '#1c1b18' },
      },
      localization: {
        timeFormatter: (t: number) => formatChartTimeKST(t),
        // 축 값은 배율 → %. (v−1)×100.
        priceFormatter: (v: number) => {
          const pc = (v - 1) * 100;
          return `${pc >= 0 ? '+' : ''}${pc.toFixed(2)}%`;
        },
      },
    });
    chartRef.current = chart;

    // 크로스헤어 시각의 값을 범례로 보낸다 — 십수 개가 겹쳐 있으면 선만 봐서는
    // 어느 게 어느 심볼인지 못 읽는다.
    chart.subscribeCrosshairMove((param) => {
      // 선 위에 커서가 올라간 심볼 → 범례 마우스오버와 같은 흐림 효과.
      // 아무 선에도 안 걸치면(param.hoveredSeries 없음) 흐림 해제.
      let hoveredSym: string | null = null;
      if (param.hoveredSeries) {
        seriesRef.current.forEach((series, sym) => {
          if (series === param.hoveredSeries) hoveredSym = sym;
        });
      }
      setChartHover(hoveredSym);

      if (!param.time || !param.seriesData || param.seriesData.size === 0) {
        setHoverPct(null);
        return;
      }
      const next = new Map<string, number>();
      seriesRef.current.forEach((series, sym) => {
        const d = param.seriesData.get(series) as LineData<UTCTimestamp> | undefined;
        if (d && typeof d.value === 'number') next.set(sym, (d.value - 1) * 100);
      });
      setHoverPct(next);
    });

    return () => {
      chart.remove();
      chartRef.current = null;
      live.clear();
      marks.clear();
      fittedRef.current = false;
    };
  }, []);

  // --- 시리즈 동기화: 데이터·수명주기 (마우스오버로는 안 돈다) ----------------
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const live = seriesRef.current;
    const showSec = tickLane; // 겹롱 · 청캐 LSK/IRYS: 틱·초
    // 사다리·틱: 전 구간이 한 화면에 들어가게 촘촘히. 휠로 넓힘.
    const oneScreen = !!selectedLadder || showSec;
    chart.applyOptions({
      timeScale: {
        secondsVisible: showSec,
        tickMarkFormatter: (t: number) => formatChartTimeKST(t, showSec),
        ...(oneScreen
          ? { barSpacing: 0.5, minBarSpacing: 0.001 }
          : { barSpacing: 6, minBarSpacing: 0.5 }),
      },
      localization: {
        timeFormatter: (t: number) => formatChartTimeKST(t, showSec),
        priceFormatter: (v: number) => {
          const pc = (v - 1) * 100;
          return `${pc >= 0 ? '+' : ''}${pc.toFixed(2)}%`;
        },
      },
    });

    // 사라진 심볼(어제 알림이 창 밖으로 나감)은 지운다.
    const wanted = new Set(drawable.map((s) => s.symbol));
    live.forEach((series, sym) => {
      if (!wanted.has(sym)) {
        chart.removeSeries(series);
        live.delete(sym);
        markersRef.current.delete(sym);
      }
    });

    drawable.forEach((s, i) => {
      const color = colors[i] ?? laneColor;
      let series = live.get(s.symbol);
      if (!series) {
        series = chart.addSeries(LineSeries, {
          color,
          lineWidth: 1,
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerRadius: 3,
        });
        live.set(s.symbol, series);
      }
      series.setData(pointsBySymbol.get(s.symbol) ?? []);

      // 화살표: 업숏은 급등폭 %, 청캐는 캐스케이드 누적 N(산리셋·quiet6 리셋).
      // 산 ▲▼ 는 DOM 음영 호스트 — 시리즈 마커에 넣으면 누적 라벨을 덮는다.
      // 15·120분 확인은 가격 바로 위 화살표. 차트 상단 고정 글자로 두지 않는다.
      const marks = buildSeriesMarkers(s, lane, color, mtnResetSec, markerLabels);
      if (lane === 'upbit' && ladderFocus(s, selectedLadder)) {
        marks.push(...mountainEdgeMarkers(s, data?.mountains ?? []));
        marks.push(...waitSeriesMarkers(s, data?.waits ?? [], waitLabel));
      }
      const mk = markersRef.current.get(s.symbol);
      if (mk) {
        mk.setMarkers(marks);
      } else {
        markersRef.current.set(s.symbol, createSeriesMarkers(series, marks));
      }
    });

    if (!drawable.length) return;
    if (selectedLadder) {
      // 버튼 = 산 전체 한 화면. 워밍 완료·봉 채워질 때마다 다시 맞춤.
      chart.timeScale().fitContent();
      fittedRef.current = true;
      return;
    }
    if (!fittedRef.current) {
      chart.timeScale().fitContent();
      fittedRef.current = true;
    }
  }, [drawable, colors, laneColor, pointsBySymbol, lane, tickLane, selectedLadder, data, mtnResetSec, markerLabels, waitLabel]);

  // --- 시리즈 스타일: 흐림·굵기·화살표 색 (마우스오버마다 여기만 돈다) --------
  // 데이터 재설정(setData)과 분리 — 선 위를 훑으면 크로스헤어가 매 프레임 바뀌는데
  // 그때마다 40개 시리즈의 봉을 다시 깔면 버벅인다.
  useEffect(() => {
    const live = seriesRef.current;
    drawable.forEach((s, i) => {
      const series = live.get(s.symbol);
      if (!series) return;
      const color = colors[i] ?? laneColor;
      // 포커스가 있으면 나머지는 흐리게 — 색 계열이 하나라 굵기만으론 안 갈린다.
      // 포커스원: 범례 마우스오버 또는 차트에서 그 선 위에 커서.
      const dim = activeFocus != null && activeFocus !== s.symbol;
      const shownColor = dim ? dimColor(color) : color;
      series.applyOptions({
        color: shownColor,
        visible: !hidden.has(s.symbol),
        lineWidth: activeFocus === s.symbol ? 3 : 1,
      });
      const mk = markersRef.current.get(s.symbol);
      if (mk) {
        const marks = buildSeriesMarkers(s, lane, shownColor, mtnResetSec, markerLabels);
        if (lane === 'upbit' && ladderFocus(s, selectedLadder)) {
          marks.push(...mountainEdgeMarkers(s, data?.mountains ?? []));
          marks.push(...waitSeriesMarkers(s, data?.waits ?? [], waitLabel));
        }
        mk.setMarkers(marks);
      }
    });
  }, [drawable, colors, laneColor, hidden, activeFocus, lane, selectedLadder, data, mtnResetSec, markerLabels, waitLabel]);

  // --- 산 구간 음영: timeScale 좌표로 밴드 위치 잡기 (여러 산) ---------------
  useEffect(() => {
    const chart = chartRef.current;
    const host = mountainBandRef.current;
    const box = boxRef.current;
    if (!chart || !host || !box) return undefined;

    const paint = () => {
      const mtns = lane === 'gyeop'
        ? []
        : lane === 'upbit'
          ? (data?.mountains ?? [])
            .filter((m) => m.first_ms > 0 && m.last_ms > 0)
            .map((m) => ({ firstMs: m.first_ms, lastMs: m.last_ms }))
          : resolveMountains(selectedLadder, data);
      host.innerHTML = '';
      if (!selectedLadder || !mtns.length || !drawable.length) {
        host.style.display = 'none';
        return;
      }
      host.style.display = 'block';
      const ts = chart.timeScale();
      const winFrom = data?.from_ms ?? 0;
      const winTo = data?.to_ms ?? Number.MAX_SAFE_INTEGER;
      // timeToCoordinate 는 시리즈에 있는 봉 시각만 받는다 — 산 first/last 를
      // 그대로 넣으면 null → 음영 전부 스킵 (2026-09-30 유저: "안보이는데").
      const focus = drawable.find((s) => (
        s.symbol === selectedLadder.symbol
        || s.perp === selectedLadder.perp
        || s.symbol.replace(/^KRW-/, '') === selectedLadder.symbol.replace(/^KRW-/, '')
      )) ?? drawable[0];
      const t0 = Math.floor((focus?.t0 ?? winFrom) / 1000);
      const step = (focus?.step_s && focus.step_s > 0) ? focus.step_s : 60;
      // n_bars 는 값이 있는 봉의 개수다. 그걸 창 길이로 쓰면 빈 분이 잘리고,
      // 그 뒤의 진입·청산이 마지막 봉(AVNT 는 14:20)에 같이 붙는다.
      const slots = focus?.c?.length ?? 1;
      const end = t0 + Math.max(0, slots - 1) * step;
      const snapSec = (ms: number) => {
        const sec = Math.floor(ms / 1000);
        const snapped = t0 + Math.floor((sec - t0) / step) * step;
        return Math.max(t0, Math.min(end, snapped));
      };
      const chartW = box.clientWidth || 0;
      mtns.forEach((m, i) => {
        // 창 밖 산 끝은 잘라서라도 그린다 (클러스터 창에 산 일부만 들어올 때).
        const aMs = Math.max(m.firstMs, winFrom);
        const bMs = Math.min(m.lastMs, winTo);
        if (!(aMs < bMs)) return;
        const x1 = ts.timeToCoordinate(snapSec(aMs) as UTCTimestamp);
        const x2 = ts.timeToCoordinate(snapSec(bMs) as UTCTimestamp);
        // 한쪽만 보이면 가장자리로 클램프 — 둘 다 null 일 때만 스킵.
        if (x1 == null && x2 == null) return;
        const left = Math.min(x1 ?? 0, x2 ?? chartW);
        const right = Math.max(x1 ?? 0, x2 ?? chartW);
        const width = Math.max(6, right - left);
        const accent = mountainAccent(i);
        const edgeL = m.firstMs >= winFrom ? `border-left:5px solid ${accent.edge}` : 'border-left:0';
        const edgeR = m.lastMs <= winTo ? `border-right:5px solid ${accent.edge}` : 'border-right:0';
        // 구간 전체 배경 — 산마다 다른 색으로 "여기부터 여기까지"가 한눈에.
        const band = document.createElement('div');
        band.style.cssText = [
          'position:absolute',
          'top:0',
          'bottom:0',
          `left:${left}px`,
          `width:${width}px`,
          `background:${accent.fill}`,
          edgeL,
          edgeR,
          'pointer-events:none',
          'box-sizing:border-box',
        ].join(';');
        host.appendChild(band);
        // 상단 색띠 — 차트 위에서 산 구간 폭이 더 또렷하게.
        const cap = document.createElement('div');
        cap.style.cssText = [
          'position:absolute',
          'top:0',
          `left:${left}px`,
          `width:${width}px`,
          'height:10px',
          `background:${accent.solid}`,
          'opacity:0.92',
          'pointer-events:none',
        ].join(';');
        host.appendChild(cap);
        const size = 22;
        // 창 안에서 실제로 시작·끝난 쪽에만 삼각형. 잘린 끝은 띠만 둔다.
        if (m.firstMs >= winFrom) {
          const up = document.createElement('div');
          up.style.cssText = [
            'position:absolute',
            `left:${left - size / 2}px`,
            'top:12px',
            'width:0',
            'height:0',
            `border-left:${size / 2}px solid transparent`,
            `border-right:${size / 2}px solid transparent`,
            `border-bottom:${size}px solid ${accent.solid}`,
            `filter:drop-shadow(0 0 6px ${accent.solid})`,
            'pointer-events:none',
          ].join(';');
          host.appendChild(up);
        }
        if (m.lastMs <= winTo) {
          const dn = document.createElement('div');
          dn.style.cssText = [
            'position:absolute',
            `left:${right - size / 2}px`,
            'bottom:10px',
            'width:0',
            'height:0',
            `border-left:${size / 2}px solid transparent`,
            `border-right:${size / 2}px solid transparent`,
            `border-top:${size}px solid ${accent.solid}`,
            `filter:drop-shadow(0 0 6px ${accent.solid})`,
            'pointer-events:none',
          ].join(';');
          host.appendChild(dn);
        }
      });
    };

    paint();
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => { paint(); })
      : null;
    ro?.observe(box);
    chart.timeScale().subscribeVisibleLogicalRangeChange(paint);
    return () => {
      ro?.disconnect();
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(paint);
      host.innerHTML = '';
      host.style.display = 'none';
    };
  }, [selectedLadder, data, drawable, data?.from_ms, data?.to_ms, lane, tickLane]);

  // 범례는 % 높은 순. API 는 최근 알림 순이라 그대로 두면 목록이 뒤섞인다.
  // 정렬 키는 라인 마지막 값(rankMetrics) — 크로스헤어 hoverPct 로 정렬하면
  // 커서를 움직일 때마다 칸이 뛰어다녀서 못 읽는다.
  const legend: LegendRow[] = useMemo(() => {
    const rows = drawable.map((s, i) => ({
      s,
      color: colors[i] ?? laneColor,
      pct: hoverPct?.get(s.symbol) ?? rankMetrics[i],
      sortPct: rankMetrics[i],
    }));
    rows.sort((a, b) => (b.sortPct ?? -Infinity) - (a.sortPct ?? -Infinity));
    return rows.map(({ s, color, pct }) => ({ s, color, pct }));
  }, [drawable, colors, laneColor, hoverPct, rankMetrics]);

  const toggle = (sym: string) => setHidden((prev) => {
    const next = new Set(prev);
    if (next.has(sym)) next.delete(sym); else next.add(sym);
    return next;
  });

  // accent=true 면 비선택도 그 색(빨강 Vision·LSK/IRYS). 기본은 선택 때만 색.
  const tabStyle = (
    active: boolean, color: string, ready: boolean, accent = false,
  ): React.CSSProperties => ({
    background: active ? `${color}26` : (accent ? `${color}14` : 'transparent'),
    border: `1px solid ${active || accent ? color : '#30363d'}`,
    color: active || accent ? color : (ready ? '#8b949e' : '#5c5750'),
    borderRadius: 6,
    padding: '5px 14px',
    fontSize: '0.78rem',
    cursor: ready ? 'pointer' : 'not-allowed',
    fontWeight: active ? 600 : 400,
  });

  return (
    <div style={{
      background: '#0d1117', minHeight: '100vh', color: '#e8dcc0', padding: 16,
      fontSize: '0.78rem',
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 10,
      }}>
        <a href={backHref} className="binance-alert-refresh" style={{ textDecoration: 'none' }}>
          {t('charts.back')}
        </a>
        <h2 style={{ margin: 0, color: laneColor, fontSize: '1rem' }}>
          {selectedLadder
            ? t(lane === 'upbit'
              ? 'charts.title.mountainLate'
              : isSkrChart(selectedLadder.perp, selectedLadder.symbol)
                ? 'charts.title.mountain36' : 'charts.title.mountain',
              { sym: selectedLadder.symbol.replace(/^KRW-/, '') })
            : t('charts.title.live')}
        </h2>
        <span style={{ color: MUTED, fontSize: '0.72rem' }}>
          {!selectedLadder && data?.kst_day ? `${data.kst_day} (KST) · ` : ''}
          {t('charts.count', { n: drawable.length })}
          {undrawable.length ? ` ${t('charts.noBars', { n: undrawable.length })}` : ''}
          {data?.truncated ? t('charts.truncated') : ''}
        </span>
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ color: MUTED, fontSize: '0.7rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={auto}
              onChange={(e) => setAuto(e.target.checked)}
              style={{ marginRight: 4 }}
            />
            {t('charts.auto')}
          </label>
          <button
            type="button"
            className="binance-alert-refresh"
            onClick={() => { void load(); }}
            disabled={loading && !data}
          >
            {t('common.refresh')}
          </button>
        </span>
      </div>

      {/* 레인 탭. 업숏·청캐는 사다리, 빗숏은 라이브스택 스탑. */}
      <div style={{
        display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap',
      }}>
        {LANES.map((l) => (
          <button
            key={l.key}
            type="button"
            onClick={() => {
              if (!l.ready || l.key === lane) return;
              setLane(l.key);
              // 다른 레인의 사다리 선택은 이 레인에서 의미가 없다 —
              // 라이브(오늘~지금)로 돌아간다.
              setSelectedLadder(null);
            }}
            disabled={!l.ready}
            title={l.ready
              ? t('charts.lane.ready', { lane: laneName(l.key) })
              : t('charts.lane.soon')}
            style={tabStyle(lane === l.key, LANE_COLORS[l.key], l.ready)}
          >
            {laneName(l.key)}
          </button>
        ))}
        <span style={{ width: 12 }} />
        <span style={{ color: MUTED, fontSize: '0.7rem' }}>{t('charts.baseline')}</span>
        {([
          ['window', t('charts.baseline.window')],
          ['alert', lane === 'liq' ? t('charts.baseline.fire') : t('charts.baseline.alert')],
          ['peak', t('charts.baseline.peak')],
        ] as [BaselineMode, string][]).map(([k, label]) => (
          <button
            key={k}
            type="button"
            onClick={() => setBaseline(k)}
            style={tabStyle(baseline === k, laneColor, true)}
          >
            {label}
          </button>
        ))}
        {drawable.length ? (
          <>
            <span style={{ width: 8 }} />
            <button
              type="button"
              className="binance-alert-refresh"
              onClick={() => setHidden(new Set(drawable.map((s) => s.symbol)))}
              disabled={hidden.size >= drawable.length}
              title={t('charts.hideAll.title')}
            >
              {t('charts.hideAll')}
            </button>
            <button
              type="button"
              className="binance-alert-refresh"
              onClick={() => setHidden(new Set())}
              disabled={hidden.size === 0}
              title={t('charts.showAll.title')}
            >
              {t('charts.showAll')}{hidden.size ? t('charts.showAll.hidden', { n: hidden.size }) : ''}
            </button>
          </>
        ) : null}
        {selectedLadder ? (
          <button
            type="button"
            className="binance-alert-refresh"
            onClick={() => setSelectedLadder(null)}
            title={t('charts.live.title')}
          >
            {t('charts.live')}
          </button>
        ) : null}
      </div>

      {/* 날짜 버튼 — ready 레인일 때만. 업숏은 후반 13건,
          빗숏은 스탑, 청캐는 캐스케이드 누적청산 ≥100. 누르면 first−24h …
          last+24h 창으로 바뀐다. */}
      {laneReady ? (
        <div style={{
          display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap',
        }}>
          <span style={{ color: MUTED, fontSize: '0.7rem' }}>
            {lane === 'bh' ? t('charts.stop') : lane === 'upbit' ? t('charts.loss') : lane === 'gyeop' ? t('charts.gyeopFast') : t('charts.ladder')}
            ({lane === 'liq' ? t('charts.hint.liq') : lane === 'bh' ? t('charts.hint.bh') : lane === 'gyeop' ? t('charts.hint.gyeop') : t('charts.hint.upbit')})
          </span>
          {ladderDaysErr ? (
            <span style={{ color: '#f85149', fontSize: '0.68rem' }}>{ladderDaysErr}</span>
          ) : null}
          {!ladderDaysErr && !ladderDays.length ? (
            <span style={{ color: MUTED, fontSize: '0.68rem' }}>{t('charts.none')}</span>
          ) : null}
          {ladderDays.map((it) => {
            const active = selectedLadder?.center_ms === it.center_ms
              && selectedLadder?.symbol === it.symbol;
            const pctLabel = it.net_pct != null && Number.isFinite(it.net_pct)
              ? fmtPct(it.net_pct) : '';
            const levelLabel = lane === 'liq'
              ? (`rung ${it.max_rung}` + (pctLabel ? ` ${pctLabel}` : ''))
              : lane === 'bh' ? t('charts.stop')
                : (lane === 'upbit' || lane === 'gyeop') && pctLabel ? pctLabel
                  : t('charts.level.step', { n: it.max_rung });
            const spanH = Math.max(0, (it.last_ms - it.first_ms) / 3_600_000);
            const perpU = (it.perp || it.symbol || '').toUpperCase().replace(/^KRW-/, '');
            // 청캐: LSK·IRYS만 빨강 — 누적청산 대조용 반사실 롱 종목.
            const liqAccent = lane === 'liq' && (perpU === 'LSKUSDT' || perpU === 'IRYSUSDT');
            const btnColor = liqAccent ? '#f85149' : laneColor;
            return (
              <button
                key={`${it.symbol}-${it.center_ms}`}
                type="button"
                onClick={() => setSelectedLadder(active ? null : it)}
                title={(lane === 'upbit' || lane === 'gyeop')
                  ? `${it.symbol.replace(/^KRW-/, '')} · ${levelLabel} · ${t('charts.marker.entry')} `
                    + fmtKST(new Date(it.center_ms).toISOString())
                  : `${it.symbol.replace(/^KRW-/, '')} · ${levelLabel} · ${t('charts.mountainWord')} `
                    + `${fmtKST(new Date(it.first_ms).toISOString())} ~ `
                    + `${fmtKST(new Date(it.last_ms).toISOString())}`
                    + ` (${spanH.toFixed(1)}h) · ${t(isSkrChart(it.perp, it.symbol) ? 'charts.spanTitle36' : 'charts.spanTitle')}`}
                style={tabStyle(active, btnColor, true, liqAccent)}
              >
                {kstMonthDay(it.center_ms)} {it.symbol.replace(/^KRW-/, '')}
                <span style={{ marginLeft: 4, opacity: 0.7 }}>{levelLabel}</span>
              </button>
            );
          })}
        </div>
      ) : null}

      {/* 청캐: Vision 1–7 ≥300% (옛 가짜 14 제외) — 빨강 버튼·가로선. */}
      {laneReady && lane === 'liq' ? (
        <div style={{
          display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap',
          paddingTop: 8, borderTop: '1px solid #30363d',
        }}>
          <span style={{ color: MUTED, fontSize: '0.7rem' }}>
            {t('charts.visionStrict')}
            ({t('charts.hint.visionStrict')})
          </span>
          {LIQ_VISION_STRICT_PEERS.length === 0 ? (
            <span style={{ color: MUTED, fontSize: '0.68rem' }}>{t('charts.none')}</span>
          ) : null}
          {LIQ_VISION_STRICT_PEERS.map((it) => {
            const active = selectedLadder?.center_ms === it.center_ms
              && selectedLadder?.symbol === it.symbol;
            const levelLabel = it.net_pct != null ? fmtPct(it.net_pct) : '';
            const spanH = Math.max(0, (it.last_ms - it.first_ms) / 3_600_000);
            return (
              <button
                key={`vision-${it.symbol}-${it.center_ms}`}
                type="button"
                onClick={() => setSelectedLadder(active ? null : it)}
                title={`${it.symbol} · ≥300% · ${levelLabel} · `
                  + `${fmtKST(new Date(it.first_ms).toISOString())} ~ `
                  + `${fmtKST(new Date(it.last_ms).toISOString())}`
                  + ` (${spanH.toFixed(1)}h)`}
                style={tabStyle(active, LIQ_VISION_PEER_COLOR, true, true)}
              >
                {kstMonthDay(it.center_ms)} {it.symbol.replace(/USDT$/, '')}
                {levelLabel ? (
                  <span style={{ marginLeft: 4, opacity: 0.7 }}>{levelLabel}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}

      <div style={{ color: MUTED, fontSize: '0.68rem', marginBottom: 8 }}>
        {t('charts.axis.lead')}
        <strong style={{ color: '#b9aa84' }}>
          {baseline === 'window' ? t('charts.axis.base.window') : baseline === 'peak' ? t('charts.axis.base.peak') : (lane === 'liq' ? t('charts.axis.base.fire') : t('charts.axis.base.alert'))}
        </strong>
        {t('charts.axis.body')}
        {baseline === 'peak' ? t('charts.axis.peakNote') : ''}
        {' '}
        {t('charts.axis.price', {
          what: lane === 'liq' ? t('charts.what.liq') : lane === 'bh' ? t('charts.what.bh')
            : lane === 'gyeop' ? t('charts.what.gyeop')
            : (lane === 'upbit' && selectedLadder) ? t('charts.what.upbitLoss') : t('charts.what.upbit'),
        })}
        {selectedLadder ? (
          <>
            {' · '}
            <strong style={{ color: laneColor }}>
              {selectedLadder.symbol.replace(/^KRW-/, '')}
            </strong>
            {' '}
            {(lane === 'gyeop'
              ? []
              : lane === 'upbit'
              ? (data?.mountains ?? [])
              : (data?.mountains?.length
                ? data.mountains
                : [{
                  first_ms: data?.cluster_first_ms ?? selectedLadder.first_ms,
                  last_ms: data?.cluster_last_ms ?? selectedLadder.last_ms,
                }])).map((m, i, arr) => (
              <React.Fragment key={`${m.first_ms}-${m.last_ms}`}>
                {i > 0 ? ' · ' : null}
                <span style={{ color: '#f0c14b' }}>▲</span>
                {fmtKST(new Date(m.first_ms).toISOString())}
                {'→'}
                <span style={{ color: '#f0c14b' }}>▼</span>
                {fmtKST(new Date(m.last_ms).toISOString())}
                {arr.length > 1 ? ` (#${i + 1})` : null}
              </React.Fragment>
            ))}
            {t('charts.window')}
            {data?.from_ms ? fmtKST(new Date(data.from_ms).toISOString()) : '…'}
            {' ~ '}
            {data?.to_ms ? fmtKST(new Date(data.to_ms).toISOString()) : '…'}
            {t(lane === 'gyeop'
              ? 'charts.span.gyeop'
              : liqFlipTick
              ? 'charts.span.liqFlip'
              : lane === 'upbit'
              ? 'charts.mountainSpanLate'
              : isSkrChart(selectedLadder.perp, selectedLadder.symbol)
                ? 'charts.mountainSpan36' : 'charts.mountainSpan')}
          </>
        ) : (
          <>
            {fetchedAt ? t('charts.updated', { t: fmtKST(new Date(fetchedAt).toISOString()) }) : ''}
          </>
        )}
        {data?.took_ms != null ? t('charts.server', { n: data.took_ms }) : ''}
        {!laneReady ? t('charts.laneSoonNote') : ''}
      </div>

      {error ? (
        <div style={{
          border: '1px solid #f8514966', background: 'rgba(248,81,73,0.1)',
          color: '#f85149', borderRadius: 6, padding: '8px 10px', marginBottom: 10,
        }}>
          {error}
        </div>
      ) : null}

      {/* 48h 과거창이 타임아웃으로 조용히 재시도하는 동안 — 화면에 지난 창의
          차트가 남아 있어도(아래 empty-state 는 안 뜬다) 여긴 항상 보인다.
          에러 색이 아니라 레인 색이다 — 실패가 아니라 "아직 로딩 중"이다
          (2026-09-08 유저: "에러 내지 말고 로딩중이라고 보여줘"). */}
      {retrying ? (
        <div style={{
          border: `1px solid ${laneColor}55`, background: `${laneColor}14`,
          color: laneColor, borderRadius: 6, padding: '8px 10px', marginBottom: 10,
          fontSize: '0.72rem',
        }}>
          {t(tickLane
            ? 'charts.loading.gyeop'
            : lane === 'upbit'
            ? 'charts.loading.mountainLate'
            : isSkrChart(selectedLadder?.perp, selectedLadder?.symbol)
              ? 'charts.loading.mountain36' : 'charts.loading.mountain', {
            n: Math.round(historicRetryRef.current * HISTORIC_RETRY_DELAY_MS / 1000),
          })}
        </div>
      ) : null}

      <div style={{
        border: `1px solid ${laneColor}33`, borderRadius: 8, background: '#12100c',
        padding: '8px 10px', marginBottom: 10, position: 'relative',
      }}>
        <div ref={boxRef} style={{ height: CHART_H, width: '100%' }} />
        {/* 산 구간 음영 호스트 — 자식으로 산마다 밴드. */}
        <div
          ref={mountainBandRef}
          aria-hidden
          style={{
            display: 'none',
            position: 'absolute',
            top: 8,
            bottom: 36,
            left: 10,
            right: 10,
            pointerEvents: 'none',
            zIndex: 5,
            overflow: 'hidden',
          }}
        />
        {!drawable.length ? (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
            justifyContent: 'center', color: MUTED, textAlign: 'center', padding: 12,
          }}>
            {loading
              ? (selectedLadder
                ? (retrying
                  ? t(tickLane
                    ? 'charts.loading.gyeop'
                    : lane === 'upbit'
                    ? 'charts.loading.mountainLate'
                    : isSkrChart(selectedLadder.perp, selectedLadder.symbol)
                      ? 'charts.loading.mountain36' : 'charts.loading.mountain', {
                    n: Math.round(historicRetryRef.current * HISTORIC_RETRY_DELAY_MS / 1000),
                  })
                  : t(tickLane
                    ? 'charts.loading.gyeopShort'
                    : lane === 'upbit'
                    ? 'charts.loading.mountainShortLate'
                    : isSkrChart(selectedLadder.perp, selectedLadder.symbol)
                      ? 'charts.loading.mountainShort36' : 'charts.loading.mountainShort'))
                : t('charts.loading.first'))
              : (selectedLadder
                ? t('charts.empty.historic', {
                  what: lane === 'liq' ? t('charts.empty.what.liq') : lane === 'bh' ? t('charts.empty.what.bh') : lane === 'gyeop' ? t('charts.empty.what.gyeop') : t('charts.empty.what.upbit'),
                })
                : t('charts.empty.today', {
                  what: lane === 'liq' ? t('charts.empty.what.liq') : lane === 'bh' ? t('charts.empty.what.bh') : t('charts.empty.what.alert'),
                }))}
          </div>
        ) : null}
      </div>

      {/* 범례 — 클릭하면 그 선을 숨긴다. 올려두면 그 선만 굵어지고 나머지는 흐려진다. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 230px), 1fr))',
        gap: '2px 10px',
      }}>
        {legend.map(({ s, color, pct }) => {
          const off = hidden.has(s.symbol);
          // 봉은 있어도(n_bars>0) 마지막이 오래됐으면 — 상시수집 대상이 아니거나
          // 상장폐지 추정(WAVES 실측: ws_batch 평생 0건, 12시간+ 정지). 이 선은
          // 24시간 창 앞쪽에서 끊긴 짧은 조각이라 다른 선들 사이에서 눈에 안 띈다
          // (2026-09-08 유저: "WAVES 는 차트에 표시되지 않고 있으니") — 흐리게
          // 두지 않고 범례에서 바로 "정지"로 짚어준다.
          const stale = s.stale_min != null;
          const staleLabel = stale
            ? t('charts.stale', {
              h: Math.floor(s.stale_min! / 60),
              m: s.stale_min! % 60,
            })
            : null;
          return (
            <button
              key={s.symbol}
              type="button"
              onClick={() => toggle(s.symbol)}
              onMouseEnter={() => setFocus(s.symbol)}
              onMouseLeave={() => setFocus(null)}
              title={t('charts.legend.title', {
                perp: s.perp,
                bars: s.n_bars,
                kind: lane === 'liq' ? t('charts.legend.kind.liq') : t('charts.legend.kind.alert'),
                n: s.alerts?.length ?? 0,
                px: fmtPrice(s.last_px),
                warming: s.warming ? t('charts.legend.warming') : '',
                note: s.note ? `\n${s.note}` : '',
              })}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, background: 'transparent',
                border: 'none', padding: '2px 4px', cursor: 'pointer', textAlign: 'left',
                color: off ? '#5c5750' : '#e8dcc0', fontSize: '0.72rem',
                opacity: off ? 0.55 : 1,
              }}
            >
              <span style={{
                width: 10, height: 10, borderRadius: 2, flex: '0 0 auto',
                background: off ? 'transparent' : color,
                border: `1px solid ${color}`,
                // 정지된 선은 칩도 빗금 느낌으로 — 색은 같은 계열이라 칩만 보고는
                // 몇 시간째 안 움직이는 선인지 구분이 안 된다.
                opacity: stale && !off ? 0.35 : 1,
              }} />
              <span style={{ textDecoration: off ? 'line-through' : 'none' }}>
                {s.symbol.replace(/^KRW-/, '')}
              </span>
              {staleLabel && !off ? (
                <span style={{ color: '#8b7d5a', fontSize: '0.62rem' }}>{staleLabel}</span>
              ) : null}
              <span style={{
                marginLeft: 'auto', fontVariantNumeric: 'tabular-nums',
                color: off ? '#5c5750' : (pct != null && pct < 0 ? '#f85149' : '#3fb950'),
              }}>
                {fmtPct(pct)}
              </span>
              {s.warming ? <span style={{ color: laneColor, fontSize: '0.6rem' }}>…</span> : null}
            </button>
          );
        })}
      </div>

      {undrawable.length ? (
        <div style={{ color: '#6e6455', fontSize: '0.66rem', marginTop: 8 }}>
          {t('charts.noBarsLine')}{undrawable.map((s) => `${s.symbol.replace(/^KRW-/, '')}(${s.note || '—'})`).join(' · ')}
        </div>
      ) : null}
    </div>
  );
};

const BinanceAlertChartsPage: React.FC<{ locale?: DashboardLocale }> = ({ locale = 'en' }) => (
  <DashboardLocaleProvider locale={locale}>
    <ChartsBody />
  </DashboardLocaleProvider>
);

export default BinanceAlertChartsPage;
